<template>
    <div class="ai-chat-messages flex flex-col flex-1 overflow-hidden">
        <div
            v-if="isLoading"
            class="flex items-center justify-center py-8"
        >
            <i class="far fa-spinner-third fa-spin text-blue-500 text-xl"></i>
        </div>

        <perfect-scrollbar
            v-else
            ref="scrollContainer"
            class="flex-1 px-4 py-3"
        >
            <div
                v-if="!messages.length && !isStreaming"
                class="flex flex-col items-center justify-center py-12 text-center"
            >
                <i class="far fa-tint text-muted text-4xl mb-3"></i>
                <p class="text-muted">
                    How can I help you today?
                </p>
            </div>

            <div
                v-for="(message, index) in groupedMessages"
                :key="index"
                class="mb-4"
            >
                <div
                    :class="[
                        'message-bubble rounded-lg px-3 py-2 text-base leading-relaxed',
                        message.isError
                            ? 'bg-yellow-100 text-yellow-800 border border-yellow-300 mr-8'
                            : message.role === 'user'
                                ? 'bg-blue-500 text-white ml-8'
                                : 'bg-gray-300 text-gray-800 mr-8',
                    ]"
                >
                    <div
                        v-if="message.isError"
                    >
                        <div class="flex items-center break-words">
                            <i class="far fa-exclamation-triangle mr-2"></i>
                            <span v-text="message.content"></span>
                        </div>
                        <button
                            v-if="getOriginalMessage(index)"
                            class="mt-2 text-xs text-yellow-700 hover:text-yellow-900 underline"
                            @click="$emit('retry', getOriginalMessage(index))"
                        >
                            <i class="far fa-rotate-right mr-1"></i>Retry
                        </button>
                    </div>
                    <div
                        v-else-if="message.role === 'assistant'"
                        class="ai-markdown break-words"
                        v-html="renderMarkdown(message.content)"
                    ></div>
                    <div v-else>
                        <div
                            v-if="message.images && message.images.length"
                            class="flex flex-wrap gap-2 mb-2"
                        >
                            <img
                                v-for="(img, imgIdx) in message.images"
                                :key="imgIdx"
                                :src="getImageSrc(img)"
                                class="h-20 max-w-32 object-cover rounded-lg cursor-pointer border border-white border-opacity-30 hover:opacity-80 transition-opacity"
                                title="Click to view full size"
                                @click="openImageFullscreen(getImageSrc(img))"
                            >
                        </div>
                        <div
                            v-if="message.content"
                            class="whitespace-pre-wrap break-words"
                            v-text="message.content"
                        ></div>
                    </div>
                </div>
                <div
                    v-if="message.role === 'assistant' && !message.isError && !isStreaming"
                    class="mt-1 ml-1 flex items-center"
                >
                    <button
                        v-if="message.id"
                        v-tooltip="{ content: 'Good response', container: '#ai-chat-panel' }"
                        class="text-xs mr-1"
                        :class="message.feedback === 'up' ? 'text-green-500' : 'text-muted hover:text-gray-700'"
                        @click="$emit('feedback', { messageId: message.id, feedback: 'up' })"
                    >
                        <i class="far fa-thumbs-up fa-fw"></i>
                    </button>
                    <button
                        v-if="message.id"
                        v-tooltip="{ content: 'Bad response', container: '#ai-chat-panel' }"
                        class="text-xs mr-2"
                        :class="message.feedback === 'down' ? 'text-red-500' : 'text-muted hover:text-gray-700'"
                        @click="$emit('feedback', { messageId: message.id, feedback: 'down' })"
                    >
                        <i class="far fa-thumbs-down fa-fw"></i>
                    </button>
                    <button
                        v-if="isLastAssistantMessage(index)"
                        v-tooltip="{ content: 'Regenerate response', container: '#ai-chat-panel' }"
                        class="text-xs text-muted hover:text-gray-700"
                        @click="$emit('regenerate')"
                    >
                        <i class="far fa-rotate-right fa-fw"></i>
                    </button>
                </div>
            </div>

            <div
                v-if="isStreaming && streamingContent"
                class="mb-4"
            >
                <div class="message-bubble rounded-lg px-3 py-2 text-base leading-relaxed bg-gray-200 text-muted mr-8">
                    <div
                        class="ai-markdown break-words"
                        v-html="renderMarkdown(streamingContent)"
                    ></div>
                </div>
            </div>

            <div
                v-if="isToolProcessing"
                class="mb-4"
            >
                <div class="message-bubble rounded-lg px-3 py-2 text-base bg-gray-200 text-muted mr-8">
                    <i class="far fa-cog fa-spin text-muted mr-1"></i>
                    <span
                        class="text-muted text-sm"
                        v-text="currentToolName ? formatToolName(currentToolName) + '...' : 'Looking up data...'"
                    ></span>
                </div>
            </div>

            <div
                v-if="isStreaming && !streamingContent && !isToolProcessing"
                class="mb-4"
            >
                <div class="message-bubble rounded-lg px-3 py-2 text-base bg-gray-200 text-muted mr-8">
                    <i class="far fa-circle-notch fa-spin text-muted"></i>
                </div>
            </div>
        </perfect-scrollbar>
    </div>
</template>

<script>
import { marked } from 'marked';
import { PerfectScrollbar } from 'vue3-perfect-scrollbar';

marked.setOptions({
    breaks: true,
    gfm: true,
});

export default {
    components: {
        PerfectScrollbar,
    },

    props: {
        messages: {
            type: Array,
            default: () => [],
        },
        conversationId: {
            type: Number,
            default: null,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        isStreaming: {
            type: Boolean,
            default: false,
        },
        isToolProcessing: {
            type: Boolean,
            default: false,
        },
        streamingContent: {
            type: String,
            default: '',
        },
        currentToolName: {
            type: String,
            default: '',
        },
    },

    computed: {
        groupedMessages() {
            const result = [];

            for (let i = 0; i < this.messages.length; i++) {
                const message = this.messages[i];

                if (message.role !== 'assistant' || message.isError) {
                    result.push(message);
                } else {
                    // Collect consecutive assistant messages (intermediate tool-use steps + final)
                    const parts = [message];
                    while (
                        i + 1 < this.messages.length
                        && this.messages[i + 1].role === 'assistant'
                        && !this.messages[i + 1].isError
                    ) {
                        i++;
                        parts.push(this.messages[i]);
                    }

                    if (parts.length === 1) {
                        result.push(message);
                    } else {
                        // Merge: use the last message's id/feedback, concatenate non-empty content
                        const last = parts[parts.length - 1];
                        const mergedContent = parts
                            .map((p) => (p.content || '').trim())
                            .filter(Boolean)
                            .join('\n\n');

                        result.push({
                            ...last,
                            content: mergedContent,
                        });
                    }
                }
            }

            return result;
        },
    },

    watch: {
        messages() {
            this.scrollToBottom();
        },
        streamingContent() {
            this.scrollToBottom();
        },
        isStreaming() {
            this.scrollToBottom();
        },
        isToolProcessing() {
            this.scrollToBottom();
        },
    },

    mounted() {
        if (this.$el) {
            this.$el.addEventListener('click', this.handleLinkClick);
        }
    },

    beforeUnmount() {
        if (this.$el) {
            this.$el.removeEventListener('click', this.handleLinkClick);
        }
    },

    methods: {
        cleanContent(content) {
            if (!content) return '';

            // Remove SUGGESTION: lines from the content
            const lines = content.split('\n');
            const cleanedLines = lines.filter((line) => !line.startsWith('SUGGESTION:'));
            return cleanedLines.join('\n').trim();
        },

        renderMarkdown(content) {
            if (!content) return '';

            const cleaned = this.cleanContent(content);
            return marked.parse(cleaned);
        },

        handleLinkClick(e) {
            const link = e.target.closest('a[href]');

            if (!link) return;

            const href = link.getAttribute('href');

            if (href && href.startsWith('/') && !href.startsWith('//')) {
                e.preventDefault();
                this.$router.replace(href).catch(() => {});
            }
        },

        getOriginalMessage(errorIndex) {
            for (let i = errorIndex - 1; i >= 0; i--) {
                if (this.groupedMessages[i].role === 'user') {
                    return this.groupedMessages[i].content;
                }
            }

            return null;
        },

        isLastAssistantMessage(index) {
            for (let i = this.groupedMessages.length - 1; i >= 0; i--) {
                if (this.groupedMessages[i].role === 'assistant') {
                    return i === index;
                }
            }

            return false;
        },

        formatToolName(name) {
            if (!name) return 'Processing...';

            const labels = {
                search_customers: 'Search Customers',
                search_orders: 'Search Orders',
                search_invoices: 'Search Invoices',
                search_contacts: 'Search Contacts',
                search_payments: 'Search Payments',
                search_proposals: 'Search Proposals',
                get_customer_details: 'Get Customer Details',
                get_order_details: 'Get Order Details',
                get_invoice_details: 'Get Invoice Details',
                get_contact_details: 'Get Contact Details',
                get_payment_details: 'Get Payment Details',
                get_proposal_details: 'Get Proposal Details',
                get_activity_history: 'Get Activity History',
                get_order_workflow_rules: 'Get Order Workflow Rules',
                get_invoice_workflow_rules: 'Get Invoice Workflow Rules',
                get_user_permissions: 'Get User Permissions',
                list_routes: 'List Routes',
                get_route_details: 'Get Route Details',
            };

            return labels[name] || name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        },

        getImageSrc(img) {
            // Live preview (just sent, has data URL)
            if (img.preview) {
                return img.preview;
            }

            // Loaded from DB — served via authenticated endpoint
            if (img.filename && this.conversationId) {
                return `/api/ai-chat/conversations/${this.conversationId}/images/${img.filename}`;
            }

            return '';
        },

        openImageFullscreen(src) {
            if (!src) return;
            window.open(src, '_blank');
        },

        scrollToBottom() {
            this.$nextTick(() => {
                const container = this.$refs.scrollContainer;

                if (!container || !container.$el) {
                    return;
                }

                if (container.ps) {
                    container.ps.update();
                }

                container.$el.scrollTop = container.$el.scrollHeight;
            });
        },
    },
};
</script>

<style scoped>
.ai-markdown :deep(h1),
.ai-markdown :deep(h2),
.ai-markdown :deep(h3) {
    font-weight: 700;
    color: #1f2937;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 0.25em;
    margin: 0.75em 0 0.4em;
}

.ai-markdown :deep(h1:first-child),
.ai-markdown :deep(h2:first-child),
.ai-markdown :deep(h3:first-child) {
    margin-top: 0;
}

.ai-markdown :deep(h1) { font-size: 1.2em; }
.ai-markdown :deep(h2) { font-size: 1.05em; }
.ai-markdown :deep(h3) { font-size: 0.95em; border-bottom: none; }

.ai-markdown :deep(p) {
    margin: 0.35em 0;
    line-height: 1.5;
}

.ai-markdown :deep(ul),
.ai-markdown :deep(ol) {
    margin: 0.35em 0;
    padding-left: 1.5em;
}

.ai-markdown :deep(li) {
    margin: 0.2em 0;
    line-height: 1.5;
}

.ai-markdown :deep(li strong) {
    color: #374151;
}

.ai-markdown :deep(code) {
    background: rgba(0, 0, 0, 0.06);
    padding: 0.1em 0.3em;
    border-radius: 3px;
    font-size: 0.9em;
}

.ai-markdown :deep(pre) {
    background: rgba(0, 0, 0, 0.06);
    padding: 0.5em;
    border-radius: 4px;
    overflow-x: auto;
    margin: 0.5em 0;
}

.ai-markdown :deep(pre code) {
    background: none;
    padding: 0;
}

.ai-markdown :deep(a) {
    color: #2563eb;
    text-decoration: underline;
}

.ai-markdown :deep(table) {
    border-collapse: collapse;
    margin: 0.5em 0;
    width: auto;
    max-width: 100%;
    display: block;
    overflow-x: auto;
    font-size: 0.85em;
    border-radius: 4px;
}

.ai-markdown :deep(th),
.ai-markdown :deep(td) {
    border: 1px solid #d1d5db;
    padding: 0.3em 0.6em;
    text-align: left;
    white-space: nowrap;
}

.ai-markdown :deep(th) {
    background: white;
    font-weight: 600;
    color: #374151;
}

.ai-markdown :deep(td) {
    background: white;
}

.ai-markdown :deep(blockquote) {
    border-left: 3px solid #93c5fd;
    background: rgba(59, 130, 246, 0.04);
    padding: 0.4em 0.75em;
    border-radius: 0 4px 4px 0;
    margin: 0.75em 0;
    color: #4b5563;
}

.ai-markdown :deep(hr) {
    border: none;
    border-top: 1px solid #e5e7eb;
    margin: 0.6em 0;
}

.ai-markdown :deep(p:last-child) {
    margin-bottom: 0;
}

.ai-markdown :deep(p:first-child) {
    margin-top: 0;
}

/* Claude Code style tool use indicator */
.tool-use-indicator {
    background: #f8f9fa;
    border: 1px solid #e2e8f0;
    border-left: 3px solid #3b82f6;
}

.tool-use-arrow {
    color: #3b82f6;
    font-size: 0.75rem;
}

.tool-use-dots::after {
    content: '';
    animation: tool-dots 1.4s infinite;
}

@keyframes tool-dots {
    0%, 20% { content: ''; }
    40% { content: '.'; }
    60% { content: '..'; }
    80%, 100% { content: '...'; }
}
</style>
